import type { FieldErrors } from '@/types'

export const REQUEST_HEADER = 'x-dfrs-request'

export class ApiError extends Error {
    status: number
    errors: FieldErrors

    constructor(message: string, status: number, errors: FieldErrors = {}) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.errors = errors
    }
}

type RequestOptions = {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    body?: unknown
}

const FALLBACK_MESSAGE = 'Terjadi kesalahan. Coba lagi sebentar lagi.'

/**
 * Browser helper for the app's own API routes. Every route answers
 * { ok: true, data } or { ok: false, message, errors }.
 */
export const apiRequest = async <T = unknown>(url: string, options: RequestOptions = {}): Promise<T> => {
    const { method = 'GET', body } = options

    let response: Response

    try {
        response = await fetch(url, {
            method,
            credentials: 'same-origin',
            headers: {
                [REQUEST_HEADER]: '1',
                ...(body === undefined ? {} : { 'Content-Type': 'application/json' })
            },
            body: body === undefined ? undefined : JSON.stringify(body)
        })
    }
    catch {
        throw new ApiError('Tidak dapat terhubung. Periksa koneksi internetmu lalu coba lagi.', 0)
    }

    const payload = await response.json().catch(() => null) as { ok?: boolean, data?: T, message?: string, errors?: FieldErrors } | null

    if (response.status === 401 && typeof window !== 'undefined' && !url.startsWith('/api/auth/')) {
        const next = `${window.location.pathname}${window.location.search}`
        window.location.href = `/masuk?next=${encodeURIComponent(next)}`
    }

    if (!response.ok || !payload?.ok) {
        throw new ApiError(payload?.message ?? FALLBACK_MESSAGE, response.status, payload?.errors ?? {})
    }

    return payload.data as T
}

export const errorMessage = (error: unknown): string => {
    if (error instanceof ApiError) return error.message
    return FALLBACK_MESSAGE
}
